import { Component, OnInit, OnDestroy } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { FormGroup, FormControl } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { BudgetMapperService } from './services/budget-mapper.service';
import { BudgetResponse, Budget } from './models/budget-response.interface';
import { ServiceConfigService } from '../../../../services/service-config.service';

@Component({
  selector: 'nh-budget-details',
  standalone: false,
  templateUrl: './budget-details.component.html',
  styleUrl: './budget-details.component.scss'
})
export class BudgetDetailsComponent implements OnInit, OnDestroy {
  letterDetails: any;
  householdMembers: any[] = [];
  selectedMember: any = null;
  categoryOfEligibility: string = '';
  eligibilityCategories: any[] = [];
  showNoBudgetAlert: boolean = false;
  showBudgetCalculation: boolean = false;
  budgetData: Budget | null = null;
  budgetResponse: BudgetResponse | null = null;
  nodBudgetResponse: any = null;
  isLoading: boolean = true;
  hasError: boolean = false;
  selectionForm = new FormGroup({
    member: new FormControl(''),
    coverageCategory: new FormControl('')
  });
  private langChangeSubscription?: Subscription;
  private noBudgetCoeCodes: string[] = [];

  constructor(
    private titleService: Title,
    private router: Router,
    private budgetMapperService: BudgetMapperService,
    private translateService: TranslateService,
    private serverConfig: ServiceConfigService
  ) {
    this.titleService.setTitle('Budget Details');
    const storedPayload = sessionStorage.getItem('nodBudgetPayload');
    if (storedPayload) {
      this.letterDetails = JSON.parse(storedPayload);
    }
  }

  ngOnInit(): void {
    this.loadConfigValues();
    this.loadBudgetData();
    this.subscribeToLanguageChanges();
  }

  private loadConfigValues(): void {
    const config = this.serverConfig.getServerConfig();
    this.noBudgetCoeCodes = config?.noBudgetCoeCodes || [];
  }

  private subscribeToLanguageChanges(): void {
    this.langChangeSubscription = this.translateService.onDefaultLangChange.subscribe(() => {
      // Save current state before language change triggers page reload
      this.saveState();
    });
  }

  private loadBudgetData(): void {
    this.isLoading = true;
    this.hasError = false;
    const payload = { coReqSeq: this.letterDetails?.coReqSeq, caseNum: this.letterDetails?.caseNum };
    this.budgetMapperService.getNodBudgetInfo(payload).subscribe({
      next: (response: BudgetResponse) => {
        this.isLoading = false;
        this.budgetResponse = response;
        this.nodBudgetResponse = response?.nodBudgetInfoWrapper?.indvs;
        // Check if response has data
        if (!this.nodBudgetResponse || (Array.isArray(this.nodBudgetResponse) && this.nodBudgetResponse?.length === 0)) {
          this.hasError = true;
          return;
        }
        this.householdMembers = this.budgetMapperService?.extractHouseholdMembers(response);
        const getSavedBudgetState = sessionStorage.getItem('budgetDetailsState');
        if (getSavedBudgetState) {
          const { memberId, coe } = JSON.parse(getSavedBudgetState);
          this.selectionForm.patchValue({ member: memberId }, { emitEvent: false });
          this.selectedMember = this.householdMembers.find(m => m.id === memberId);
          this.eligibilityCategories = this.budgetMapperService.extractCategories(this.budgetResponse, parseInt(memberId));
          this.selectionForm.patchValue({ coverageCategory: coe }, { emitEvent: false });
          
          // Check if restored COE is in no-budget list
          if (this.noBudgetCoeCodes?.includes(coe)) {
            this.showNoBudgetAlert = true;
            this.showBudgetCalculation = false;
          } else {
            this.budgetData = this.budgetMapperService.getBudgetForMemberAndCategory(this.budgetResponse, parseInt(memberId), coe);
            this.showBudgetCalculation = true;
          }
          sessionStorage.removeItem('budgetDetailsState');
        }
      },
      error: (error) => {
        this.isLoading = false;
        this.hasError = true;
        console.error('Error loading budget data:', error);
      }
    });
  }

  saveState(): void {
    const memberId = this.selectionForm.get('member')?.value;
    const coe = this.selectionForm.get('coverageCategory')?.value;
    if (memberId && coe) {
      sessionStorage.setItem('budgetDetailsState', JSON.stringify({ memberId, coe }));
    }
  }

  onMemberSelect() {
    const memberId = this.selectionForm.get('member')?.value;
    if (memberId && this.budgetResponse) {
      this.selectedMember = this.householdMembers?.find(m => m.id === memberId);
      // Reset state when switching members
      this.showNoBudgetAlert = false;
      this.showBudgetCalculation = false;
      this.budgetData = null; // Clear previous budget data
      this.selectionForm.get('coverageCategory')?.setValue('');
      // Check if member has budget info (truly empty array)
      if (this.selectedMember && !this.selectedMember.hasBudgetInfo) {
        this.showNoBudgetAlert = true;
        this.eligibilityCategories = [];
      } else {
        // Extract categories for selected member (filters out R01 and SST)
        this.eligibilityCategories = this.budgetMapperService.extractCategories(
          this.budgetResponse,
          parseInt(memberId)
        );
        
        // If no valid categories remain after filtering, show NO_BUDGET_INFO_MESSAGE
        if (this.eligibilityCategories?.length === 0) {
          this.showNoBudgetAlert = true;
        }
      }
      // Save state after member selection
      this.saveState();
    }
  }

  onCategorySelect() {
    const categoryCode = this.selectionForm.get('coverageCategory')?.value;
    const memberId = this.selectionForm.get('member')?.value;
    // Clear previous data immediately when switching categories
    this.budgetData = null;
    this.showBudgetCalculation = false;
    this.showNoBudgetAlert = false;

    if (categoryCode && memberId && this.budgetResponse) {
      // Check if selected COE is in no-budget list
      if (this.noBudgetCoeCodes?.includes(categoryCode)) {
        this.showNoBudgetAlert = true;
        this.showBudgetCalculation = false;
        this.saveState();
        return;
      }

      // Get budget data for selected member and category
      this.budgetData = this.budgetMapperService.getBudgetForMemberAndCategory(this.budgetResponse,
        parseInt(memberId),
        categoryCode
      );

      // Check if budget type is FM, ABD, MAGI, CK to show calculation
      const budgetTypes = ['FM', 'ABD', 'MAGI', 'CK']
      if (this.budgetData && !budgetTypes.includes(this.budgetData?.type)) {
        this.showNoBudgetAlert = true;
        this.showBudgetCalculation = false;
      } else if (this.budgetData && this.budgetData?.type !== null) {
        // Only show calculation if data was found and type is not null
        this.showNoBudgetAlert = false;
        this.showBudgetCalculation = true;
      }
      // Save state after category selection
      this.saveState();
    }
  }

  backToMyLetters() {
    sessionStorage.removeItem('nodBudgetPayload');
    sessionStorage.removeItem('budgetDetailsState');
    this.router.navigate(['/user-profile/letters/my-letters']);
  }

  ngOnDestroy(): void {
    if (this.langChangeSubscription) {
      this.langChangeSubscription.unsubscribe();
    }
  }
}

